import { PersonPageFragment } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import IconLegend, { IconLegendItem } from "@components/IconLegend";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import { GlobalPageProps, slugify } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import { WindowWidth } from "@lib/hooks/WindowWidth";
import { useMobile } from "@lib/hooks/useMobile";
import { ReactElement } from "react";
import { JsonLd } from "react-schemaorg";
import { Person } from "schema-dts";

export type PersonPageProps = ComponentProps<
  PersonPageFragment
>;

export default function PersonPage({ id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, hero, informationSection, personJobTitle, googleScholar, linkedin, instagram, twitter, pageUrl }: PersonPageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;
  const is960 = useMobile(960);
  const isMobile = useMobile(WindowWidth.Mobile);

  const iconOrder = [
    'role',
    'area-of-work',
    'specialisms',
    'contact-details',
    'contact-links',
    'google-scholar',
  ];

  let links = [];
  if (googleScholar) links.push(googleScholar);
  if (linkedin) links.push(linkedin);
  if (twitter) links.push(twitter);
  if (instagram) links.push(instagram);

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      <JsonLd<Person>
        item={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": heroProps?.title || title,
          "description": metatags?.metaDescription || '',
          "image": metatags?.metaImage?.md,
          "jobTitle": personJobTitle || undefined,
          "sameAs": links && links.length ? links : undefined,
        }}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection?.tiles?.length &&
          <GridContainer>
            <GridWrapper alignment={"full-width"}>
              <IconLegend
                toggle={isMobile}
                toggleItem="role"
                toggleLabel="Key information"
                ariaLabel="Information"
                grid={isMobile ? 1 : is960 ? 3 : 5}
                items={[...informationSection.tiles]
                  .sort((a, b) => {
                    const aIndex = iconOrder.indexOf(slugify(a.label));
                    const bIndex = iconOrder.indexOf(slugify(b.label));
                    return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                  })
                  .map(({ label, value }) => ({
                    type: '' as IconLegendItem['type'],
                    label,
                    info: value ? `<div class="legend-item-info">${value}</div>` : undefined,
                  }))}
              />
            </GridWrapper>
          </GridContainer>}
        {content && <Slices {...content} />}
      </main>
    </>
  );
}
